import React, { useState, useEffect } from 'react';
import {
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Chip,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  TableSortLabel,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  CircularProgress
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import apiService from '../../services/api';

type Order = 'asc' | 'desc';
type OrderBy = 'business_name' | 'state' | 'city' | 'lead_score' | 'status';

const LeadsPage: React.FC = () => {
  const [leads, setLeads] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<OrderBy>('lead_score');
  const [emailDialog, setEmailDialog] = useState<{open: boolean, leadId: string, businessName: string}>({open: false, leadId: '', businessName: ''});
  const [emailDiscovering, setEmailDiscovering] = useState<string | null>(null);
  const [emailResults, setEmailResults] = useState<any>(null);

  useEffect(() => {
    loadLeads();
    loadStats();
  }, []);

  const loadLeads = async () => {
    try {
      const response = await apiService.getLeads({}, { page: 1, limit: 1000 });
      setLeads(response.leads || []);
    } catch (error) {
      console.error('Failed to load leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const statsData = await apiService.getLeadsStats();
      setStats(statsData);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleRequestSort = (property: OrderBy) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedLeads = React.useMemo(() => {
    return [...leads].sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      // Handle null/undefined values
      if (aValue == null) aValue = '';
      if (bValue == null) bValue = '';

      // Convert to strings for comparison if needed
      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();

      if (order === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  }, [leads, order, orderBy]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new': return 'primary';
      case 'contacted': return 'info';
      case 'qualified': return 'success';
      case 'interested': return 'warning';
      default: return 'default';
    }
  };

  const handleEmailDiscovery = async (leadId: string, businessName: string) => {
    setEmailDiscovering(leadId);
    try {
      const results = await apiService.discoverEmail(leadId);
      setEmailResults(results);
      setEmailDialog({ open: true, leadId, businessName });

      // If email was found and updated, refresh the leads to show new email
      if (results.emailUpdated) {
        loadLeads();
      }
    } catch (error) {
      console.error('Email discovery failed:', error);
    } finally {
      setEmailDiscovering(null);
    }
  };

  const handleCloseEmailDialog = () => {
    setEmailDialog({ open: false, leadId: '', businessName: '' });
    setEmailResults(null);
  };

  if (loading) {
    return <Typography>Loading leads...</Typography>;
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Leads Management
      </Typography>

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Leads
                </Typography>
                <Typography variant="h5">{stats.total_count || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  New Leads
                </Typography>
                <Typography variant="h5">{stats.new_count || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  With Email
                </Typography>
                <Typography variant="h5">{stats.with_email_count || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Avg Score
                </Typography>
                <Typography variant="h5">{Math.round(stats.avg_score || 0)}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Leads Table */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">All Leads</Typography>
            <Button variant="outlined" href="/search">
              Find New Leads
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'business_name'}
                      direction={orderBy === 'business_name' ? order : 'asc'}
                      onClick={() => handleRequestSort('business_name')}
                    >
                      Business Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'state'}
                      direction={orderBy === 'state' ? order : 'asc'}
                      onClick={() => handleRequestSort('state')}
                    >
                      Location (State)
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'lead_score'}
                      direction={orderBy === 'lead_score' ? order : 'asc'}
                      onClick={() => handleRequestSort('lead_score')}
                    >
                      Score
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'status'}
                      direction={orderBy === 'status' ? order : 'asc'}
                      onClick={() => handleRequestSort('status')}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedLeads.map((lead) => (
                  <TableRow key={lead.id}>
                    <TableCell>
                      <Typography variant="subtitle2">{lead.business_name}</Typography>
                      {lead.website && (
                        <Typography variant="caption" color="textSecondary">
                          <a href={lead.website} target="_blank" rel="noopener noreferrer">
                            {lead.website}
                          </a>
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {lead.city}, {lead.state}
                      </Typography>
                    </TableCell>
                    <TableCell>{lead.phone || 'N/A'}</TableCell>
                    <TableCell>{lead.email || 'N/A'}</TableCell>
                    <TableCell>
                      <Chip
                        label={lead.lead_score}
                        color={lead.lead_score >= 80 ? 'success' : lead.lead_score >= 60 ? 'warning' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={lead.status}
                        color={getStatusColor(lead.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Find Email">
                        <IconButton
                          size="small"
                          onClick={() => handleEmailDiscovery(lead.id, lead.business_name)}
                          disabled={emailDiscovering === lead.id}
                        >
                          {emailDiscovering === lead.id ? (
                            <CircularProgress size={20} />
                          ) : (
                            <SearchIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {leads.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="textSecondary">
                No leads found
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Start by searching for new leads
              </Typography>
              <Button variant="contained" href="/search">
                Find New Leads
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Email Discovery Results Dialog */}
      <Dialog open={emailDialog.open} onClose={handleCloseEmailDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Email Discovery Results for {emailDialog.businessName}
        </DialogTitle>
        <DialogContent>
          {emailResults && (
            <Box>
              {emailResults.foundEmails && emailResults.foundEmails.length > 0 ? (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" color="success.main" gutterBottom>
                    ✓ Found {emailResults.foundEmails.length} Email{emailResults.foundEmails.length > 1 ? 's' : ''}
                  </Typography>
                  <List>
                    {emailResults.foundEmails.map((email: string, index: number) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={email}
                          secondary={index === 0 && emailResults.emailUpdated ? 'Updated in lead record' : ''}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              ) : (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" color="warning.main" gutterBottom>
                    No emails found via website scraping
                  </Typography>
                </Box>
              )}

              {emailResults.suggestedEmails && emailResults.suggestedEmails.length > 0 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Suggested Email Patterns
                  </Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    These are common email patterns for this domain. Try contacting these addresses:
                  </Typography>
                  <List>
                    {emailResults.suggestedEmails.slice(0, 10).map((email: string, index: number) => (
                      <ListItem key={index}>
                        <ListItemText primary={email} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="caption" color="textSecondary">
                  Discovery methods used: {emailResults.method?.join(', ')}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEmailDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeadsPage;